import { useState, useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  Search,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Mail,
  Plus,
  MoreHorizontal,
  Shield,
  Loader2,
  DollarSign,
  CreditCard,
  Edit,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  TrendingUp,
  Pencil,
  Eye
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { LineChart, Line } from 'recharts';
import EditAgencyDialog from '@/components/admin/EditAgencyDialog';
import { useImpersonation } from '@/hooks/useImpersonation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Agency {
  id: string;
  name: string;
  code: string;
  contact_email: string | null;
  plan: string;
  is_approved: boolean;
  created_at: string;
  updated_at: string;
  logo_url: string | null;
  custom_price_override: number | null;
  price_override_notes: string | null;
  address?: string | null;
  phone?: string | null;
  manager_name?: string | null;
  num_employees?: number | null;
  website?: string | null;
  whatsapp?: string | null;
  state_billing_rate?: number | null;
  caregiver_rate?: number | null;
  patient_care_hours?: number | null;
  _count?: {
    patients: number;
    users: number;
    sessions: number;
  };
}

type PlanType = 'free' | 'startup' | 'mid_level' | 'enterprise';

// Fallback config for backwards compatibility - will be overridden by database
const PLAN_CONFIG: Record<string, { quota: number; price: number; displayName: string }> = {
  free: { quota: 100, price: 0, displayName: 'Free' },
  startup: { quota: 5000, price: 199, displayName: 'StartUp' },
  mid_level: { quota: 10000, price: 399, displayName: 'Mid Level' },
  enterprise: { quota: 20000, price: 799, displayName: 'Enterprise' },
};

interface DbPlan {
  id: string;
  plan_key: string;
  name: string;
  price: number;
  patient_limit: number | null;
  supervisor_limit: number | null;
  is_active: boolean;
  sort_order: number;
}

interface AgencyDetails extends Agency {
  users: { email: string; role: string; is_active: boolean }[];
}

interface BillingEditState {
  plan: PlanType;
  customPriceOverride: string;
  priceOverrideNotes: string;
}

export default function AdminAgenciesPage() {
  const { user } = useAdminAuth();
  const { toast } = useToast();
  const { openImpersonationView, canImpersonate } = useImpersonation();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAgencies, setFilteredAgencies] = useState<Agency[]>([]);
  const [showAddAgency, setShowAddAgency] = useState(false);
  const [addingAgency, setAddingAgency] = useState(false);
  const [agencyToApprove, setAgencyToApprove] = useState<Agency | null>(null);
  const [agencyToReject, setAgencyToReject] = useState<Agency | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedAgency, setSelectedAgency] = useState<AgencyDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Billing edit state
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingEdit, setBillingEdit] = useState<BillingEditState>({
    plan: 'free',
    customPriceOverride: '',
    priceOverrideNotes: ''
  });
  const [savingBilling, setSavingBilling] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Sorting state
  type SortField = 'name' | 'plan' | 'price' | 'created_at';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [newAgency, setNewAgency] = useState({
    name: '',
    code: '',
    contact_email: ''
  });

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPlanDialogOpen, setBulkPlanDialogOpen] = useState(false);
  const [bulkPlan, setBulkPlan] = useState<PlanType>('startup');
  const [applyingBulkPlan, setApplyingBulkPlan] = useState(false);
  
  // Filter state
  const [planFilter, setPlanFilter] = useState<PlanType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending'>('all');
  const [agencyFilter, setAgencyFilter] = useState<string>('all');
  
  // Edit agency state
  const [editingAgency, setEditingAgency] = useState<Agency | null>(null);
  const [editAgencyDialogOpen, setEditAgencyDialogOpen] = useState(false);
  
  // Price override quick dialog state
  const [priceOverrideAgency, setPriceOverrideAgency] = useState<Agency | null>(null);
  const [priceOverrideValue, setPriceOverrideValue] = useState<string>('');
  const [priceOverrideNotes, setPriceOverrideNotes] = useState<string>('');
  const [savingPriceOverride, setSavingPriceOverride] = useState(false);
  
  // Forecast scenario state
  type ForecastScenario = 'conservative' | 'moderate' | 'aggressive' | 'custom';
  const [forecastScenario, setForecastScenario] = useState<ForecastScenario>('moderate');
  const [customGrowthRate, setCustomGrowthRate] = useState<string>('5');

  // Database plans state
  const [dbPlans, setDbPlans] = useState<DbPlan[]>([]);

  // Build plan config from database or use fallback
  const planConfigFromDb = dbPlans.reduce((acc, plan) => {
    acc[plan.plan_key] = {
      quota: plan.patient_limit || 0,
      price: plan.price,
      displayName: plan.name,
    };
    return acc;
  }, {} as Record<string, { quota: number; price: number; displayName: string }>);
  
  const effectivePlanConfig = dbPlans.length > 0 ? planConfigFromDb : PLAN_CONFIG;

  const loadDbPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('id, plan_key, name, price, patient_limit, supervisor_limit, is_active, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setDbPlans(data || []);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  useEffect(() => {
    if (user?.role === 'super_admin') {
      loadAgencies();
      loadDbPlans();
    }
  }, [user]);

  useEffect(() => {
    let filtered = agencies.filter(agency => {
      // Agency filter (by specific agency id)
      if (agencyFilter !== 'all' && agency.id !== agencyFilter) {
        return false;
      }
      // Plan filter
      if (planFilter !== 'all' && agency.plan !== planFilter) {
        return false;
      }
      // Status filter
      if (statusFilter === 'approved' && !agency.is_approved) {
        return false;
      }
      if (statusFilter === 'pending' && agency.is_approved) {
        return false;
      }
      // Search filter
      return agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (agency.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    });
    
    // Sort the filtered results
    const planOrder: Record<string, number> = { free: 0, startup: 1, mid_level: 2, enterprise: 3 };
    
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'plan':
          comparison = (planOrder[a.plan] ?? 0) - (planOrder[b.plan] ?? 0);
          break;
        case 'price':
          const priceA = a.custom_price_override ?? (PLAN_CONFIG[a.plan as PlanType]?.price ?? 0);
          const priceB = b.custom_price_override ?? (PLAN_CONFIG[b.plan as PlanType]?.price ?? 0);
          comparison = priceA - priceB;
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    setFilteredAgencies(filtered);
    setCurrentPage(1);
  }, [agencies, searchTerm, sortField, sortDirection, planFilter, statusFilter, agencyFilter]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-3.5 w-3.5" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3.5 w-3.5" /> 
      : <ArrowDown className="h-3.5 w-3.5" />;
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredAgencies.length / PAGE_SIZE);
  const paginatedAgencies = filteredAgencies.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Bulk selection helpers
  const allPaginatedSelected = paginatedAgencies.length > 0 && paginatedAgencies.every(a => selectedIds.has(a.id));
  const somePaginatedSelected = paginatedAgencies.some(a => selectedIds.has(a.id));
  
  const toggleSelectAll = () => {
    if (allPaginatedSelected) {
      const newSet = new Set(selectedIds);
      paginatedAgencies.forEach(a => newSet.delete(a.id));
      setSelectedIds(newSet);
    } else {
      const newSet = new Set(selectedIds);
      paginatedAgencies.forEach(a => newSet.add(a.id));
      setSelectedIds(newSet);
    }
  };

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleBulkPlanUpdate = async () => {
    if (selectedIds.size === 0) return;
    
    setApplyingBulkPlan(true);
    try {
      const ids = Array.from(selectedIds);
      
      const { error } = await supabase
        .from('agencies')
        .update({ plan: bulkPlan })
        .in('id', ids);

      if (error) throw error;

      // Update local state
      setAgencies(prev => prev.map(a => 
        selectedIds.has(a.id) ? { ...a, plan: bulkPlan } : a
      ));

      toast({
        title: "Plans Updated",
        description: `Updated ${selectedIds.size} agencies to ${PLAN_CONFIG[bulkPlan].displayName} plan.`,
      });
      
      setSelectedIds(new Set());
      setBulkPlanDialogOpen(false);
    } catch (error: any) {
      console.error('Error updating plans:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update plans',
        variant: 'destructive',
      });
    } finally {
      setApplyingBulkPlan(false);
    }
  };

  const loadAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get counts for each agency
      const agenciesWithCounts = await Promise.all(
        (data || []).map(async (agency) => {
          const [{ count: patientCount }, { count: userCount }, { count: sessionCount }] = await Promise.all([
            supabase
              .from('patients')
              .select('*', { count: 'exact', head: true })
              .eq('agency_id', agency.id),
            supabase
              .from('profiles')
              .select('*', { count: 'exact', head: true })
              .eq('agency_id', agency.id),
            supabase
              .from('triage_sessions')
              .select('*', { count: 'exact', head: true })
              .eq('agency_id', agency.id),
          ]);

          return {
            ...agency,
            _count: {
              patients: patientCount || 0,
              users: userCount || 0,
              sessions: sessionCount || 0,
            },
          };
        })
      );

      setAgencies(agenciesWithCounts);
    } catch (error) {
      console.error('Error loading agencies:', error);
      toast({
        title: "Error",
        description: "Failed to load agencies",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAgency = async () => {
    if (!agencyToApprove) return;
    setProcessingId(agencyToApprove.id);

    try {
      const { error } = await supabase
        .from('agencies')
        .update({ is_approved: true })
        .eq('id', agencyToApprove.id);

      if (error) throw error;

      toast({
        title: "Agency Approved",
        description: `${agencyToApprove.name} has been approved and can now use the platform.`,
      });

      loadAgencies();
    } catch (error: any) {
      console.error('Error approving agency:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to approve agency",
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
      setAgencyToApprove(null);
    }
  };

  const handleRejectAgency = async () => {
    if (!agencyToReject) return;
    setProcessingId(agencyToReject.id);

    try {
      const { error } = await supabase
        .from('agencies')
        .update({ is_approved: false })
        .eq('id', agencyToReject.id);

      if (error) throw error;

      toast({
        title: "Agency Access Revoked",
        description: `${agencyToReject.name} has been suspended from the platform.`,
      });

      loadAgencies();
    } catch (error: any) {
      console.error('Error rejecting agency:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update agency",
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
      setAgencyToReject(null);
    }
  };

  const handleViewDetails = async (agency: Agency) => {
    setLoadingDetails(true);
    setEditingBilling(false);
    try {
      const { data: users } = await supabase
        .from('profiles')
        .select('email, role, is_active')
        .eq('agency_id', agency.id)
        .order('email');

      const details: AgencyDetails = {
        ...agency,
        users: users || [],
      };
      
      setSelectedAgency(details);
      setBillingEdit({
        plan: (agency.plan as PlanType) || 'free',
        customPriceOverride: agency.custom_price_override?.toString() || '',
        priceOverrideNotes: agency.price_override_notes || ''
      });
    } catch (error) {
      console.error('Error loading agency details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load agency details',
        variant: 'destructive',
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveBilling = async () => {
    if (!selectedAgency) return;
    
    setSavingBilling(true);
    try {
      const overrideValue = billingEdit.customPriceOverride ? parseFloat(billingEdit.customPriceOverride) : null;
      
      const { error } = await supabase
        .from('agencies')
        .update({
          plan: billingEdit.plan,
          custom_price_override: overrideValue,
          price_override_notes: billingEdit.priceOverrideNotes || null
        })
        .eq('id', selectedAgency.id);

      if (error) throw error;

      // Update local state
      setSelectedAgency(prev => prev ? {
        ...prev,
        plan: billingEdit.plan,
        custom_price_override: overrideValue,
        price_override_notes: billingEdit.priceOverrideNotes || null
      } : null);
      
      // Update agencies list
      setAgencies(prev => prev.map(a => 
        a.id === selectedAgency.id 
          ? { ...a, plan: billingEdit.plan, custom_price_override: overrideValue, price_override_notes: billingEdit.priceOverrideNotes || null }
          : a
      ));

      toast({
        title: "Billing Updated",
        description: `Billing settings for ${selectedAgency.name} have been updated.`,
      });
      setEditingBilling(false);
    } catch (error: any) {
      console.error('Error saving billing:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save billing settings',
        variant: 'destructive',
      });
    } finally {
      setSavingBilling(false);
    }
  };

  const handleOpenPriceOverride = (agency: Agency) => {
    setPriceOverrideAgency(agency);
    setPriceOverrideValue(agency.custom_price_override?.toString() || '');
    setPriceOverrideNotes(agency.price_override_notes || '');
  };

  const handleSavePriceOverride = async () => {
    if (!priceOverrideAgency) return;
    
    setSavingPriceOverride(true);
    try {
      const overrideValue = priceOverrideValue.trim() ? parseFloat(priceOverrideValue) : null;
      
      if (priceOverrideValue.trim() && isNaN(overrideValue!)) {
        toast({
          title: 'Invalid Price',
          description: 'Please enter a valid number for the price override.',
          variant: 'destructive',
        });
        setSavingPriceOverride(false);
        return;
      }
      
      const { error } = await supabase
        .from('agencies')
        .update({
          custom_price_override: overrideValue,
          price_override_notes: priceOverrideNotes.trim() || null
        })
        .eq('id', priceOverrideAgency.id);

      if (error) throw error;

      // Update local state
      setAgencies(prev => prev.map(a => 
        a.id === priceOverrideAgency.id 
          ? { ...a, custom_price_override: overrideValue, price_override_notes: priceOverrideNotes.trim() || null }
          : a
      ));

      toast({
        title: "Price Override Saved",
        description: overrideValue !== null 
          ? `Custom price of $${overrideValue}/mo set for ${priceOverrideAgency.name}.`
          : `Price override removed for ${priceOverrideAgency.name}.`,
      });
      setPriceOverrideAgency(null);
    } catch (error: any) {
      console.error('Error saving price override:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save price override',
        variant: 'destructive',
      });
    } finally {
      setSavingPriceOverride(false);
    }
  };

  const handleClearPriceOverride = async () => {
    if (!priceOverrideAgency) return;
    
    setSavingPriceOverride(true);
    try {
      const { error } = await supabase
        .from('agencies')
        .update({
          custom_price_override: null,
          price_override_notes: null
        })
        .eq('id', priceOverrideAgency.id);

      if (error) throw error;

      setAgencies(prev => prev.map(a => 
        a.id === priceOverrideAgency.id 
          ? { ...a, custom_price_override: null, price_override_notes: null }
          : a
      ));

      toast({
        title: "Price Override Cleared",
        description: `${priceOverrideAgency.name} will now use standard plan pricing.`,
      });
      setPriceOverrideAgency(null);
    } catch (error: any) {
      console.error('Error clearing price override:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to clear price override',
        variant: 'destructive',
      });
    } finally {
      setSavingPriceOverride(false);
    }
  };

  const getEffectivePrice = (agency: Agency) => {
    if (agency.custom_price_override !== null) {
      return agency.custom_price_override;
    }
    return PLAN_CONFIG[agency.plan as PlanType]?.price ?? 0;
  };

  const handleEditBilling = async (agency: Agency) => {
    setLoadingDetails(true);
    setEditingBilling(true);
    try {
      const { data: users } = await supabase
        .from('profiles')
        .select('email, role, is_active')
        .eq('agency_id', agency.id)
        .order('email');

      const details: AgencyDetails = {
        ...agency,
        users: users || [],
      };
      
      setSelectedAgency(details);
      setBillingEdit({
        plan: (agency.plan as PlanType) || 'free',
        customPriceOverride: agency.custom_price_override?.toString() || '',
        priceOverrideNotes: agency.price_override_notes || ''
      });
    } catch (error) {
      console.error('Error loading agency details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load agency details',
        variant: 'destructive',
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleAddAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingAgency(true);

    try {
      // Generate a unique code if not provided
      const code = newAgency.code.trim() || newAgency.name.substring(0, 4).toUpperCase() + Date.now().toString().slice(-4);

      const { error } = await supabase
        .from('agencies')
        .insert({
          name: newAgency.name.trim(),
          code: code.toUpperCase(),
          contact_email: newAgency.contact_email.trim() || null,
          is_approved: true // Super admin created agencies are pre-approved
        });

      if (error) throw error;

      toast({
        title: "Agency Created",
        description: `${newAgency.name} has been created and approved.`,
      });

      setNewAgency({ name: '', code: '', contact_email: '' });
      setShowAddAgency(false);
      loadAgencies();
    } catch (error: any) {
      console.error('Error adding agency:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create agency",
        variant: "destructive",
      });
    } finally {
      setAddingAgency(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const approvedCount = agencies.filter(a => a.is_approved).length;
  const pendingCount = agencies.filter(a => !a.is_approved).length;

  if (user?.role !== 'super_admin') {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-lg font-medium text-foreground">Access Denied</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Only super admins can access this page.
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-10 bg-muted rounded"></div>
            <div className="grid grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col space-y-2">
            <h1 className="text-3xl font-bold text-foreground">Agency Management</h1>
            <p className="text-muted-foreground">
              Manage and approve healthcare providers (Dr Offices / Home Care Agencies)
            </p>
          </div>
          <Button onClick={() => setShowAddAgency(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Agency
          </Button>
        </div>

        {/* Search, Filter & Sort */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search agencies by name, code, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={agencyFilter} onValueChange={(value) => setAgencyFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by agency" />
              </SelectTrigger>
              <SelectContent className="bg-popover z-50 max-h-[300px]">
                <SelectItem value="all">All Agencies</SelectItem>
                {agencies
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Select value={planFilter} onValueChange={(value) => setPlanFilter(value as PlanType | 'all')}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by plan" />
              </SelectTrigger>
              <SelectContent className="bg-popover z-50">
                <SelectItem value="all">All Plans</SelectItem>
                {dbPlans.length > 0 ? (
                  dbPlans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.plan_key}>
                      {plan.name}
                    </SelectItem>
                  ))
                ) : (
                  <>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="startup">StartUp</SelectItem>
                    <SelectItem value="mid_level">Mid Level</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as 'all' | 'approved' | 'pending')}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-popover z-50">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            {(planFilter !== 'all' || statusFilter !== 'all' || agencyFilter !== 'all' || searchTerm) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPlanFilter('all');
                  setStatusFilter('all');
                  setAgencyFilter('all');
                  setSearchTerm('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Clear filters
              </Button>
            )}
          </div>
          <div className="flex gap-1 flex-wrap">
            <span className="text-sm text-muted-foreground flex items-center mr-2">Sort by:</span>
            <Button
              variant={sortField === 'name' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('name')}
              className="gap-1"
            >
              Name {getSortIcon('name')}
            </Button>
            <Button
              variant={sortField === 'plan' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('plan')}
              className="gap-1"
            >
              Plan {getSortIcon('plan')}
            </Button>
            <Button
              variant={sortField === 'price' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('price')}
              className="gap-1"
            >
              Price {getSortIcon('price')}
            </Button>
            <Button
              variant={sortField === 'created_at' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('created_at')}
              className="gap-1"
            >
              Date {getSortIcon('created_at')}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Building2 className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{agencies.length}</p>
                  <p className="text-sm text-muted-foreground">Total Agencies</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{approvedCount}</p>
                  <p className="text-sm text-muted-foreground">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-2xl font-bold text-foreground">{pendingCount}</p>
                  <p className="text-sm text-muted-foreground">Pending Approval</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {agencies.reduce((sum, a) => sum + (a._count?.patients || 0), 0)}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Patients</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Analytics by Plan */}
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Monthly Revenue by Plan
            </CardTitle>
            <CardDescription>
              Estimated monthly recurring revenue based on agency plans and custom pricing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {(['free', 'startup', 'mid_level', 'enterprise'] as PlanType[]).map((plan) => {
                const planAgencies = agencies.filter(a => a.plan === plan && a.is_approved);
                const revenue = planAgencies.reduce((sum, a) => {
                  const price = a.custom_price_override ?? PLAN_CONFIG[plan].price;
                  return sum + price;
                }, 0);
                const customCount = planAgencies.filter(a => a.custom_price_override !== null).length;
                
                return (
                  <div key={plan} className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      {PLAN_CONFIG[plan].displayName}
                    </p>
                    <p className="text-2xl font-bold text-foreground mt-1">
                      ${revenue.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {planAgencies.length} {planAgencies.length === 1 ? 'agency' : 'agencies'}
                      {customCount > 0 && ` (${customCount} custom)`}
                    </p>
                  </div>
                );
              })}
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-xs text-primary uppercase tracking-wide font-medium">
                  Total MRR
                </p>
                <p className="text-2xl font-bold text-primary mt-1">
                  ${agencies
                    .filter(a => a.is_approved)
                    .reduce((sum, a) => {
                      const price = a.custom_price_override ?? PLAN_CONFIG[a.plan as PlanType]?.price ?? 0;
                      return sum + price;
                    }, 0)
                    .toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {agencies.filter(a => a.is_approved).length} active agencies
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MRR Growth Trend Chart */}
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              MRR Growth Over Time
            </CardTitle>
            <CardDescription>
              Monthly recurring revenue trend based on agency sign-up dates
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              // Calculate MRR over time based on agency created_at dates
              const approvedAgencies = agencies.filter(a => a.is_approved);
              
              if (approvedAgencies.length === 0) {
                return (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No approved agencies yet
                  </div>
                );
              }

              // Get date range - from first agency to now
              const sortedByDate = [...approvedAgencies].sort(
                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
              );
              const firstDate = new Date(sortedByDate[0].created_at);
              const now = new Date();
              
              // Generate monthly data points
              const monthlyData: { month: string; mrr: number; agencies: number }[] = [];
              const currentDate = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
              
              while (currentDate <= now) {
                const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
                
                // Count agencies that existed by this month end
                const activeAgencies = approvedAgencies.filter(
                  a => new Date(a.created_at) <= monthEnd
                );
                
                const mrr = activeAgencies.reduce((sum, a) => {
                  const price = a.custom_price_override ?? PLAN_CONFIG[a.plan as PlanType]?.price ?? 0;
                  return sum + price;
                }, 0);
                
                monthlyData.push({
                  month: currentDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                  mrr,
                  agencies: activeAgencies.length
                });
                
                currentDate.setMonth(currentDate.getMonth() + 1);
              }

              // Calculate growth
              const currentMrr = monthlyData[monthlyData.length - 1]?.mrr ?? 0;
              const previousMrr = monthlyData[monthlyData.length - 2]?.mrr ?? 0;
              const growthPercent = previousMrr > 0 
                ? ((currentMrr - previousMrr) / previousMrr * 100).toFixed(1)
                : '0';

              return (
                <div className="space-y-4">
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="text-muted-foreground">Current MRR: </span>
                      <span className="font-semibold">${currentMrr.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">MoM Growth: </span>
                      <span className={`font-semibold ${Number(growthPercent) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Number(growthPercent) >= 0 ? '+' : ''}{growthPercent}%
                      </span>
                    </div>
                  </div>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 12 }} 
                          className="text-muted-foreground"
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }} 
                          className="text-muted-foreground"
                          tickFormatter={(value) => `$${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
                                  <p className="font-medium text-foreground">{data.month}</p>
                                  <p className="text-sm text-primary">MRR: ${data.mrr.toLocaleString()}</p>
                                  <p className="text-sm text-muted-foreground">{data.agencies} agencies</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="mrr"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          fill="url(#mrrGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Revenue Forecast Projection */}
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Revenue Forecast
                </CardTitle>
                <CardDescription>
                  Projected MRR for the next 6 months with different growth scenarios
                </CardDescription>
              </div>
              <Tabs value={forecastScenario} onValueChange={(v) => setForecastScenario(v as typeof forecastScenario)}>
                <TabsList className="grid grid-cols-4 w-auto">
                  <TabsTrigger value="conservative" className="text-xs px-3">Conservative</TabsTrigger>
                  <TabsTrigger value="moderate" className="text-xs px-3">Moderate</TabsTrigger>
                  <TabsTrigger value="aggressive" className="text-xs px-3">Aggressive</TabsTrigger>
                  <TabsTrigger value="custom" className="text-xs px-3">Custom</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const approvedAgencies = agencies.filter(a => a.is_approved);
              
              if (approvedAgencies.length === 0) {
                return (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No data available for forecasting
                  </div>
                );
              }

              // Calculate historical monthly MRR
              const sortedByDate = [...approvedAgencies].sort(
                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
              );
              const firstDate = new Date(sortedByDate[0].created_at);
              const now = new Date();
              
              const historicalData: { month: string; mrr: number }[] = [];
              const currentDate = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
              
              while (currentDate <= now) {
                const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
                const activeAgencies = approvedAgencies.filter(a => new Date(a.created_at) <= monthEnd);
                const mrr = activeAgencies.reduce((sum, a) => {
                  return sum + (a.custom_price_override ?? PLAN_CONFIG[a.plan as PlanType]?.price ?? 0);
                }, 0);
                
                historicalData.push({
                  month: currentDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                  mrr
                });
                currentDate.setMonth(currentDate.getMonth() + 1);
              }

              // Calculate average historical growth rate
              const recentMonths = historicalData.slice(-Math.min(3, historicalData.length));
              let avgGrowthRate = 0;
              
              if (recentMonths.length >= 2) {
                const growthRates: number[] = [];
                for (let i = 1; i < recentMonths.length; i++) {
                  const prev = recentMonths[i - 1].mrr;
                  const curr = recentMonths[i].mrr;
                  if (prev > 0) {
                    growthRates.push((curr - prev) / prev);
                  }
                }
                avgGrowthRate = growthRates.length > 0 
                  ? growthRates.reduce((a, b) => a + b, 0) / growthRates.length 
                  : 0.05;
              } else {
                avgGrowthRate = 0.05;
              }

              // Define scenario growth rates
              const scenarioRates = {
                conservative: Math.max(avgGrowthRate * 0.5, 0.02), // 50% of avg or min 2%
                moderate: avgGrowthRate,
                aggressive: avgGrowthRate * 1.5 + 0.03, // 150% of avg + 3%
                custom: parseFloat(customGrowthRate) / 100 || 0.05
              };

              const selectedRate = scenarioRates[forecastScenario];
              const lastMrr = historicalData[historicalData.length - 1]?.mrr ?? 0;

              // Generate projections for all scenarios
              const generateProjections = (rate: number) => {
                const projections: number[] = [];
                for (let i = 0; i < 6; i++) {
                  projections.push(Math.round(lastMrr * Math.pow(1 + rate, i + 1)));
                }
                return projections;
              };

              const conservativeProj = generateProjections(scenarioRates.conservative);
              const moderateProj = generateProjections(scenarioRates.moderate);
              const aggressiveProj = generateProjections(scenarioRates.aggressive);
              const customProj = generateProjections(scenarioRates.custom);

              // Create chart data combining history and projections
              const projectionMonths: string[] = [];
              const projDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
              for (let i = 0; i < 6; i++) {
                projectionMonths.push(projDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }));
                projDate.setMonth(projDate.getMonth() + 1);
              }

              const chartData = [
                ...historicalData.slice(-4).map(d => ({
                  month: d.month,
                  actual: d.mrr,
                  conservative: null as number | null,
                  moderate: null as number | null,
                  aggressive: null as number | null,
                  custom: null as number | null
                })),
                // Bridge point - last actual value
                {
                  month: historicalData[historicalData.length - 1]?.month,
                  actual: lastMrr,
                  conservative: lastMrr,
                  moderate: lastMrr,
                  aggressive: lastMrr,
                  custom: lastMrr
                },
                ...projectionMonths.map((month, i) => ({
                  month,
                  actual: null as number | null,
                  conservative: conservativeProj[i],
                  moderate: moderateProj[i],
                  aggressive: aggressiveProj[i],
                  custom: customProj[i]
                }))
              ];

              // Remove duplicate bridge point if exists
              const uniqueChartData = chartData.filter((d, i, arr) => 
                i === 0 || d.month !== arr[i - 1].month
              );

              const selectedProj = forecastScenario === 'conservative' ? conservativeProj 
                : forecastScenario === 'aggressive' ? aggressiveProj 
                : forecastScenario === 'custom' ? customProj 
                : moderateProj;

              const projectedTotal6Mo = selectedProj.reduce((sum, v) => sum + v, 0);

              return (
                <div className="space-y-4">
                  {forecastScenario === 'custom' && (
                    <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                      <Label className="text-sm whitespace-nowrap">Growth Rate:</Label>
                      <Slider
                        value={[parseFloat(customGrowthRate) || 5]}
                        onValueChange={(v) => setCustomGrowthRate(v[0].toString())}
                        min={0}
                        max={30}
                        step={0.5}
                        className="flex-1"
                      />
                      <span className="text-sm font-medium w-12 text-right">{customGrowthRate}%</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Current MRR</p>
                      <p className="text-lg font-bold text-foreground">${lastMrr.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Historical Avg</p>
                      <p className="text-lg font-bold text-foreground">{(avgGrowthRate * 100).toFixed(1)}%</p>
                    </div>
                    <div className={`p-3 rounded-lg border ${
                      forecastScenario === 'conservative' ? 'bg-blue-500/10 border-blue-500/30' :
                      forecastScenario === 'aggressive' ? 'bg-green-500/10 border-green-500/30' :
                      forecastScenario === 'custom' ? 'bg-orange-500/10 border-orange-500/30' :
                      'bg-primary/10 border-primary/20'
                    }`}>
                      <p className="text-xs text-muted-foreground">Selected Rate</p>
                      <p className="text-lg font-bold">{(selectedRate * 100).toFixed(1)}%</p>
                    </div>
                    <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                      <p className="text-xs text-primary">6-Mo Projection</p>
                      <p className="text-lg font-bold text-primary">${selectedProj[5]?.toLocaleString() ?? 0}</p>
                    </div>
                    <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                      <p className="text-xs text-primary">6-Mo Total</p>
                      <p className="text-lg font-bold text-primary">${projectedTotal6Mo.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={uniqueChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 11 }} 
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 11 }} 
                          tickFormatter={(value) => `$${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip 
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
                                  <p className="font-medium text-foreground mb-1">{label}</p>
                                  {payload.map((p: any) => (
                                    p.value !== null && (
                                      <p key={p.dataKey} className="text-sm" style={{ color: p.color }}>
                                        {p.dataKey === 'actual' ? 'Actual' : 
                                         p.dataKey.charAt(0).toUpperCase() + p.dataKey.slice(1)}: ${p.value.toLocaleString()}
                                      </p>
                                    )
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="actual"
                          stroke="hsl(var(--foreground))"
                          strokeWidth={2}
                          dot={{ fill: 'hsl(var(--foreground))', r: 3 }}
                          connectNulls={false}
                        />
                        {(forecastScenario === 'conservative' || forecastScenario === 'moderate') && (
                          <Line
                            type="monotone"
                            dataKey="conservative"
                            stroke="#3b82f6"
                            strokeWidth={forecastScenario === 'conservative' ? 2.5 : 1.5}
                            strokeDasharray={forecastScenario === 'conservative' ? '0' : '5 5'}
                            strokeOpacity={forecastScenario === 'conservative' ? 1 : 0.5}
                            dot={forecastScenario === 'conservative' ? { fill: '#3b82f6', r: 3 } : false}
                            connectNulls
                          />
                        )}
                        <Line
                          type="monotone"
                          dataKey="moderate"
                          stroke="hsl(var(--primary))"
                          strokeWidth={forecastScenario === 'moderate' ? 2.5 : 1.5}
                          strokeDasharray={forecastScenario === 'moderate' ? '0' : '5 5'}
                          strokeOpacity={forecastScenario === 'moderate' ? 1 : 0.5}
                          dot={forecastScenario === 'moderate' ? { fill: 'hsl(var(--primary))', r: 3 } : false}
                          connectNulls
                        />
                        {(forecastScenario === 'aggressive' || forecastScenario === 'moderate') && (
                          <Line
                            type="monotone"
                            dataKey="aggressive"
                            stroke="#22c55e"
                            strokeWidth={forecastScenario === 'aggressive' ? 2.5 : 1.5}
                            strokeDasharray={forecastScenario === 'aggressive' ? '0' : '5 5'}
                            strokeOpacity={forecastScenario === 'aggressive' ? 1 : 0.5}
                            dot={forecastScenario === 'aggressive' ? { fill: '#22c55e', r: 3 } : false}
                            connectNulls
                          />
                        )}
                        {forecastScenario === 'custom' && (
                          <Line
                            type="monotone"
                            dataKey="custom"
                            stroke="#f97316"
                            strokeWidth={2.5}
                            dot={{ fill: '#f97316', r: 3 }}
                            connectNulls
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-0.5 bg-foreground" />
                      <span>Historical</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-0.5 bg-[#3b82f6]" />
                      <span>Conservative ({(scenarioRates.conservative * 100).toFixed(1)}%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-0.5 bg-primary" />
                      <span>Moderate ({(scenarioRates.moderate * 100).toFixed(1)}%)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-0.5 bg-[#22c55e]" />
                      <span>Aggressive ({(scenarioRates.aggressive * 100).toFixed(1)}%)</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Agencies List */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Agencies</CardTitle>
                <CardDescription>
                  {filteredAgencies.length} of {agencies.length} agencies
                  {selectedIds.size > 0 && ` • ${selectedIds.size} selected`}
                </CardDescription>
              </div>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Clear Selection
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setBulkPlanDialogOpen(true)}
                  >
                    <CreditCard className="w-4 h-4 mr-1" />
                    Update Plans ({selectedIds.size})
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {filteredAgencies.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-medium text-foreground">No agencies found</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {searchTerm ? 'Try adjusting your search terms.' : 'No agencies have been registered yet.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Select All Row */}
                <div className="flex items-center gap-3 px-4 py-2 border-b">
                  <Checkbox
                    checked={allPaginatedSelected}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all on this page"
                  />
                  <span className="text-sm text-muted-foreground">
                    {allPaginatedSelected ? 'Deselect all' : 'Select all on this page'}
                  </span>
                </div>
                
                {paginatedAgencies.map((agency) => (
                  <div
                    key={agency.id}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                      selectedIds.has(agency.id)
                        ? 'border-primary bg-primary/5'
                        : agency.is_approved 
                          ? 'border-border hover:bg-accent/50' 
                          : 'border-yellow-200 bg-yellow-50/50 dark:border-yellow-900 dark:bg-yellow-900/10'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <Checkbox
                        checked={selectedIds.has(agency.id)}
                        onCheckedChange={() => toggleSelect(agency.id)}
                        aria-label={`Select ${agency.name}`}
                      />
                      <Avatar className="h-12 w-12 rounded-lg">
                        {agency.logo_url ? (
                          <AvatarImage src={agency.logo_url} alt={`${agency.name} logo`} className="object-cover" />
                        ) : null}
                        <AvatarFallback className={`rounded-lg ${
                          agency.is_approved ? 'bg-primary/10' : 'bg-yellow-100 dark:bg-yellow-900/30'
                        }`}>
                          <Building2 className={`h-6 w-6 ${
                            agency.is_approved ? 'text-primary' : 'text-yellow-600'
                          }`} />
                        </AvatarFallback>
                      </Avatar>
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-foreground">{agency.name}</h3>
                          <Badge variant="outline" className="text-xs font-mono">
                            {agency.code}
                          </Badge>
                          {agency.is_approved ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approved
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          {agency.contact_email && (
                            <div className="flex items-center space-x-1">
                              <Mail className="h-3 w-3" />
                              <span>{agency.contact_email}</span>
                            </div>
                          )}
                          <div className="flex items-center space-x-1">
                            <Users className="h-3 w-3" />
                            <span>{agency._count?.patients || 0} patients</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {/* Billing Summary */}
                      <div className="text-right border-r pr-4 mr-2">
                        <div className="flex items-center gap-1.5 justify-end">
                          <Badge variant="secondary" className="text-xs capitalize">
                            {PLAN_CONFIG[agency.plan as PlanType]?.displayName || agency.plan}
                          </Badge>
                          {agency.custom_price_override !== null && (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
                              Custom
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium text-foreground mt-0.5">
                          ${getEffectivePrice(agency)}/mo
                        </p>
                      </div>
                      {/* Price Override Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenPriceOverride(agency)}
                        className="text-xs"
                      >
                        <DollarSign className="w-3 h-3 mr-1" />
                        Price
                      </Button>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">
                          Created {formatDate(agency.created_at)}
                        </p>
                      </div>
                      {!agency.is_approved ? (
                        <Button
                          size="sm"
                          onClick={() => setAgencyToApprove(agency)}
                          disabled={processingId === agency.id}
                        >
                          {processingId === agency.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-popover z-50">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => {
                              setEditingAgency(agency);
                              setEditAgencyDialogOpen(true);
                            }}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit Agency
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewDetails(agency)}>
                              <Building2 className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditBilling(agency)}>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Edit Billing
                            </DropdownMenuItem>
                            {user?.role === 'super_admin' && (
                              <DropdownMenuItem 
                                onClick={() => openImpersonationView({
                                  type: 'agency_admin',
                                  agencyId: agency.id,
                                  agencyName: agency.name,
                                })}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View as Agency Admin
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setAgencyToReject(agency)}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Revoke Access
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredAgencies.length)} of {filteredAgencies.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      // Show first, last, current and adjacent pages
                      if (page === 1 || page === totalPages) return true;
                      if (Math.abs(page - currentPage) <= 1) return true;
                      return false;
                    })
                    .reduce<(number | 'ellipsis')[]>((acc, page, idx, arr) => {
                      if (idx > 0) {
                        const prev = arr[idx - 1];
                        if (page - prev > 1) acc.push('ellipsis');
                      }
                      acc.push(page);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === 'ellipsis' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">…</span>
                      ) : (
                        <Button
                          key={item}
                          variant={currentPage === item ? 'default' : 'outline'}
                          size="sm"
                          className="min-w-[36px]"
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </Button>
                      )
                    )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Agency Dialog */}
      <Dialog open={showAddAgency} onOpenChange={setShowAddAgency}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Agency</DialogTitle>
            <DialogDescription>
              Create a new healthcare provider. They will be automatically approved.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddAgency}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="agency-name">Agency Name *</Label>
                <Input
                  id="agency-name"
                  value={newAgency.name}
                  onChange={(e) => setNewAgency(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Metro Health Clinic"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agency-code">Agency Code</Label>
                <Input
                  id="agency-code"
                  value={newAgency.code}
                  onChange={(e) => setNewAgency(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="MHC001 (auto-generated if empty)"
                  maxLength={10}
                />
                <p className="text-xs text-muted-foreground">
                  Unique identifier for this agency. Will be auto-generated if left empty.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="agency-email">Contact Email</Label>
                <Input
                  id="agency-email"
                  type="email"
                  value={newAgency.contact_email}
                  onChange={(e) => setNewAgency(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="admin@metrohealthclinic.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddAgency(false)} disabled={addingAgency}>
                Cancel
              </Button>
              <Button type="submit" disabled={addingAgency || !newAgency.name.trim()}>
                {addingAgency && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Agency
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation */}
      <AlertDialog open={!!agencyToApprove} onOpenChange={() => setAgencyToApprove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Agency</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to approve <strong>{agencyToApprove?.name}</strong>? 
              Their patients will be able to access the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApproveAgency}>
              Approve Agency
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Confirmation */}
      <AlertDialog open={!!agencyToReject} onOpenChange={() => setAgencyToReject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Agency Access</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke access for <strong>{agencyToReject?.name}</strong>? 
              Their patients will no longer be able to login or use the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRejectAgency}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Agency Details Dialog */}
      <Dialog open={!!selectedAgency} onOpenChange={() => setSelectedAgency(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedAgency?.name}
            </DialogTitle>
            <DialogDescription>
              Agency code: <span className="font-mono">{selectedAgency?.code}</span>
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : selectedAgency ? (
            <div className="space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Status & Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Status</p>
                  {selectedAgency.is_approved ? (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approved
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                      <Clock className="w-3 h-3 mr-1" />
                      Pending Approval
                    </Badge>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Contact Email</p>
                  <p className="font-medium">{selectedAgency.contact_email || '—'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{formatDate(selectedAgency.created_at)}</p>
                </div>
              </div>

              {/* Billing Section */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Billing & Pricing
                  </h4>
                  {!editingBilling && (
                    <Button variant="outline" size="sm" onClick={() => setEditingBilling(true)}>
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
                
                {editingBilling ? (
                  <div className="space-y-4 bg-muted/50 p-4 rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="billing-plan">Subscription Plan</Label>
                      <Select
                        value={billingEdit.plan}
                        onValueChange={(value) => setBillingEdit(prev => ({ ...prev, plan: value as PlanType }))}
                      >
                        <SelectTrigger id="billing-plan">
                          <SelectValue placeholder="Select plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free - $0/mo (100 sessions)</SelectItem>
                          <SelectItem value="startup">StartUp - $199/mo (5,000 sessions)</SelectItem>
                          <SelectItem value="mid_level">Mid Level - $399/mo (10,000 sessions)</SelectItem>
                          <SelectItem value="enterprise">Enterprise - $799/mo (20,000 sessions)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="price-override">
                        Custom Price Override
                        <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                      </Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="price-override"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Leave empty to use standard pricing"
                          value={billingEdit.customPriceOverride}
                          onChange={(e) => setBillingEdit(prev => ({ ...prev, customPriceOverride: e.target.value }))}
                          className="pl-9"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Standard price: ${PLAN_CONFIG[billingEdit.plan]?.price ?? 0}/month
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="override-notes">Price Override Notes</Label>
                      <Textarea
                        id="override-notes"
                        placeholder="Reason for custom pricing (e.g., contract negotiation, special discount)"
                        value={billingEdit.priceOverrideNotes}
                        onChange={(e) => setBillingEdit(prev => ({ ...prev, priceOverrideNotes: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button onClick={handleSaveBilling} disabled={savingBilling}>
                        {savingBilling && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => setEditingBilling(false)} disabled={savingBilling}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 bg-muted/50 p-4 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Plan</p>
                      <p className="font-medium capitalize">
                        {PLAN_CONFIG[selectedAgency.plan as PlanType]?.displayName || selectedAgency.plan}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Monthly Price</p>
                      <p className="font-medium flex items-center gap-1">
                        ${getEffectivePrice(selectedAgency)}/mo
                        {selectedAgency.custom_price_override !== null && (
                          <Badge variant="secondary" className="text-xs ml-1">Custom</Badge>
                        )}
                      </p>
                    </div>
                    {selectedAgency.price_override_notes && (
                      <div className="col-span-2 space-y-1">
                        <p className="text-sm text-muted-foreground">Override Notes</p>
                        <p className="text-sm">{selectedAgency.price_override_notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Users className="h-5 w-5 mx-auto text-primary mb-1" />
                    <p className="text-2xl font-bold">{selectedAgency._count?.patients ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Patients</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Shield className="h-5 w-5 mx-auto text-primary mb-1" />
                    <p className="text-2xl font-bold">{selectedAgency._count?.users ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Staff</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Clock className="h-5 w-5 mx-auto text-primary mb-1" />
                    <p className="text-2xl font-bold">{selectedAgency._count?.sessions ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Triage Sessions</p>
                  </CardContent>
                </Card>
              </div>

              {/* Users */}
              <div className="space-y-2">
                <h4 className="font-medium">Staff Users</h4>
                {selectedAgency.users.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No staff users found.</p>
                ) : (
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                    {selectedAgency.users.map((u, idx) => (
                      <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="truncate">{u.email}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize text-xs">
                            {u.role.replace('_', ' ')}
                          </Badge>
                          {!u.is_active && (
                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            {selectedAgency && !selectedAgency.is_approved && (
              <Button onClick={() => { setAgencyToApprove(selectedAgency); setSelectedAgency(null); }}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Approve Agency
              </Button>
            )}
            {selectedAgency?.is_approved && (
              <Button
                variant="destructive"
                onClick={() => { setAgencyToReject(selectedAgency); setSelectedAgency(null); }}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Revoke Access
              </Button>
            )}
            <Button variant="outline" onClick={() => setSelectedAgency(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Plan Update Dialog */}
      <Dialog open={bulkPlanDialogOpen} onOpenChange={setBulkPlanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Plans for {selectedIds.size} Agencies</DialogTitle>
            <DialogDescription>
              Select a new plan to apply to all selected agencies. This will not affect custom price overrides.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bulk-plan">New Plan</Label>
              <Select
                value={bulkPlan}
                onValueChange={(value) => setBulkPlan(value as PlanType)}
              >
                <SelectTrigger id="bulk-plan">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent className="bg-popover z-50">
                  <SelectItem value="free">Free - $0/mo (100 sessions)</SelectItem>
                  <SelectItem value="startup">StartUp - $199/mo (5,000 sessions)</SelectItem>
                  <SelectItem value="mid_level">Mid Level - $399/mo (10,000 sessions)</SelectItem>
                  <SelectItem value="enterprise">Enterprise - $799/mo (20,000 sessions)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-muted/50 p-3 rounded-lg text-sm">
              <p className="font-medium mb-1">Selected agencies:</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {agencies.filter(a => selectedIds.has(a.id)).map(a => (
                  <div key={a.id} className="flex items-center justify-between text-muted-foreground">
                    <span>{a.name}</span>
                    <span className="text-xs">{PLAN_CONFIG[a.plan as PlanType]?.displayName || a.plan} → {PLAN_CONFIG[bulkPlan].displayName}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPlanDialogOpen(false)} disabled={applyingBulkPlan}>
              Cancel
            </Button>
            <Button onClick={handleBulkPlanUpdate} disabled={applyingBulkPlan}>
              {applyingBulkPlan && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Update {selectedIds.size} Agencies
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Agency Dialog */}
      <EditAgencyDialog
        agency={editingAgency}
        open={editAgencyDialogOpen}
        onOpenChange={setEditAgencyDialogOpen}
        onAgencyUpdated={loadAgencies}
        isSuperAdmin={true}
      />

      {/* Price Override Quick Dialog */}
      <Dialog open={!!priceOverrideAgency} onOpenChange={(open) => !open && setPriceOverrideAgency(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Price Override
            </DialogTitle>
            <DialogDescription>
              Set a custom price for <strong>{priceOverrideAgency?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="quick-price-override">Custom Monthly Price</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="quick-price-override"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Leave empty to use standard pricing"
                  value={priceOverrideValue}
                  onChange={(e) => setPriceOverrideValue(e.target.value)}
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Standard plan price: ${PLAN_CONFIG[priceOverrideAgency?.plan as PlanType]?.price ?? 0}/month ({PLAN_CONFIG[priceOverrideAgency?.plan as PlanType]?.displayName || priceOverrideAgency?.plan})
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="quick-override-notes">Notes (optional)</Label>
              <Textarea
                id="quick-override-notes"
                placeholder="Reason for custom pricing (e.g., contract negotiation, special discount)"
                value={priceOverrideNotes}
                onChange={(e) => setPriceOverrideNotes(e.target.value)}
                rows={2}
              />
            </div>

            {priceOverrideAgency?.custom_price_override !== null && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Current override: <strong>${priceOverrideAgency?.custom_price_override}/mo</strong>
                </p>
                {priceOverrideAgency?.price_override_notes && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    {priceOverrideAgency.price_override_notes}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {priceOverrideAgency?.custom_price_override !== null && (
              <Button
                variant="outline"
                onClick={handleClearPriceOverride}
                disabled={savingPriceOverride}
                className="text-destructive hover:text-destructive"
              >
                Clear Override
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setPriceOverrideAgency(null)} disabled={savingPriceOverride}>
                Cancel
              </Button>
              <Button onClick={handleSavePriceOverride} disabled={savingPriceOverride}>
                {savingPriceOverride && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}